import { useEffect, useMemo, useRef, useState } from 'react';
import { AdminContentBlock, Spinner } from '../../../../common/index.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS.jsx';
import ReservationSequenceForm from './ReservationSequenceForm.jsx';
import { useSelector } from 'react-redux';
import {
  useGetReservationSequenceQuery,
  useGetSubjectsQuery,
  useSaveReservationSequenceMutation,
  useGetSequenceConflictsMutation,
  useGetCollidedSubjectsForSequenceMutation,
} from '../../../../../redux/services/reservations.jsx';
import { useGetTeachersQuery } from '../../../../../redux/services/users.jsx';
import { useGetRoomsForReservationQuery } from '../../../../../redux/services/rooms.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Form as AntdForm } from 'antd';
import { DATETIME_FORMAT_TIME } from '../../../../../utils/helpers/CommonHelper.jsx';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_ROOM_RESERVATIONS_ADMIN } from '../../../../../utils/constants/roles.js';
import dayjs from 'dayjs';

const DEBOUNCE_MS = 500;

const ReservationSequencesEdit = ({ mode }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [form] = AntdForm.useForm();
  const navigate = useNavigate();
  const user = useSelector((reduxStore) => reduxStore.auth.user);
  const currentReservationUserId =
    user?.person_id === null || user?.person_id === undefined
      ? user?.id === null || user?.id === undefined
        ? undefined
        : String(user.id)
      : String(user.person_id);
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const isRoomReservationsAdmin = useUserHasRole(ROLE_ROOM_RESERVATIONS_ADMIN);

  const pageTitle = isCreate
    ? t('Pridanie žiadosti o rezerváciu s opakovaním')
    : t('Úprava žiadosti o rezerváciu s opakovaním');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]); // Runs on mount

  const { saveSuccess: showSaveSuccess, apiErrorMessage } = useToastMessages();

  const [getCollidedSubjects, { data: collidedSubjects }] =
    useGetCollidedSubjectsForSequenceMutation();
  const [getSequenceConflicts] = useGetSequenceConflictsMutation();
  const {
    data: reservationSequenceDetail,
    isLoading: isLoadingReservationSequence,
  } = useGetReservationSequenceQuery(id, { skip: !isEdit });
  const [saveReservationSequence] = useSaveReservationSequenceMutation();

  const { data: teachers, isLoading: isLoadingTeachers } =
    useGetTeachersQuery();
  const { data: rooms, isLoading: isLoadingRooms } =
    useGetRoomsForReservationQuery();
  const { data: subjects, isLoading: isLoadingSubjects } =
    useGetSubjectsQuery();

  const [timeConflicts, setTimeConflicts] = useState(null);
  const [timeConflictsChecked, setTimeConflictsChecked] = useState(false);
  const [hasAnyTerms, setHasAnyTerms] = useState(false);

  const watchRoomId = AntdForm.useWatch('room_id', form);
  const watchSubjectCode = AntdForm.useWatch('subject_code', form);
  const watchDateFrom = AntdForm.useWatch('date_from', form);
  const watchDateTo = AntdForm.useWatch('date_to', form);
  const watchTimeFrom = AntdForm.useWatch('time_from', form);
  const watchTimeTo = AntdForm.useWatch('time_to', form);
  const watchOnMonday = AntdForm.useWatch('on_monday', form);
  const watchOnTuesday = AntdForm.useWatch('on_tuesday', form);
  const watchOnWednesday = AntdForm.useWatch('on_wednesday', form);
  const watchOnThursday = AntdForm.useWatch('on_thursday', form);
  const watchOnFriday = AntdForm.useWatch('on_friday', form);
  const watchAllDay = AntdForm.useWatch('all_day', form);

  // Department rooms for select
  const roomsOfDepartmentForSelect = useMemo(() => {
    if (!rooms) return [];
    return rooms
      .filter((room) => room.department_room === 'Yes')
      .map((room) => ({
        label: `${room.title} (${room.department_code})`,
        value: room.id,
      }));
  }, [rooms]);

   // Other rooms for select
   // In create/edit mode, exclude other department rooms (marked by is_department_room)
   const roomsOtherForSelect = useMemo(() => {
     if (!rooms) return [];
     return rooms
       .filter((room) => {
         // In create/edit mode, exclude department rooms from other departments
          if (
            (isCreate || isEdit) &&
            !isRoomReservationsAdmin &&
            room.is_department_room === true
          ) {
           return false;
         }
         return room.department_room !== 'Yes';
       })
       .map((room) => ({
         label: `${room.title} (${room.department_code})`,
         value: room.id,
       }));
    }, [rooms, isCreate, isEdit, isRoomReservationsAdmin]);

  // Get teachers for FormSelect, sorted by last name
  const teachersForSelect = useMemo(() => {
    if (!teachers) return [];
    const options = teachers
      .map((teacher) => ({
        label: teacher.full_name,
        value: String(teacher.person_id),
        last_name: teacher.last_name,
      }))
      .sort((a, b) => a.last_name.localeCompare(b.last_name));

    if (
      currentReservationUserId &&
      !options.some((teacher) => teacher.value === currentReservationUserId)
    ) {
      options.unshift({
        label:
          user?.full_name ||
          user?.fullName ||
          user?.mail ||
          currentReservationUserId,
        value: currentReservationUserId,
        last_name: user?.last_name || '',
      });
    }

    return options;
  }, [teachers, currentReservationUserId, user]);

  // Subjects for select
  const subjectsForSelect = useMemo(() => {
    if (!subjects) return [];
    return subjects.map((subject) => ({
      label: subject.name,
      value: subject.code,
    }));
  }, [subjects]);

  // Fetch time conflicts when room, from or to changes
  useEffect(() => {
    // Guard: only when at least one term exists
    if (!hasAnyTerms) {
      setTimeConflicts(null);
      setTimeConflictsChecked(false);
      return;
    }

    if (watchRoomId && watchDateFrom && watchDateTo) {
      // Skip conflict check when date validators report errors
      const hasDateErrors = form
        .getFieldsError(['date_from', 'date_to'])
        .some(
          (field) => Array.isArray(field.errors) && field.errors.length > 0,
        );
      if (hasDateErrors) {
        setTimeConflicts(null);
        setTimeConflictsChecked(false);
        return;
      }

      // Manual validation mirroring the form rules to avoid premature API calls
      const fromD = dayjs(watchDateFrom);
      const toD = dayjs(watchDateTo);
      if (!fromD.isValid() || !toD.isValid()) {
        setTimeConflicts(null);
        setTimeConflictsChecked(false);
        return;
      }
      if (watchAllDay) {
        if (toD.startOf('day').isBefore(fromD.startOf('day'))) {
          setTimeConflicts(null);
          setTimeConflictsChecked(false);
          return;
        }
      } else {
        if (!toD.isAfter(fromD)) {
          setTimeConflicts(null);
          setTimeConflictsChecked(false);
          return;
        }
      }

      const fromVal = watchAllDay ? fromD.startOf('day') : fromD;
      const toVal = watchAllDay ? toD.endOf('day') : toD;

      const payload = {
        room_id: Number(watchRoomId),
        date_from: fromVal.set('second', 0).format(),
        date_to: toVal.set('second', 0).format(),
        all_day: watchAllDay ? 1 : 0,
        time_from: watchAllDay
          ? '00:00'
          : (dayjs.isDayjs(watchTimeFrom)
              ? watchTimeFrom
              : dayjs(watchTimeFrom)
            ).format('HH:mm'),
        time_to: watchAllDay
          ? '23:59'
          : (dayjs.isDayjs(watchTimeTo)
              ? watchTimeTo
              : dayjs(watchTimeTo)
            ).format('HH:mm'),
        on_monday: !!watchOnMonday,
        on_tuesday: !!watchOnTuesday,
        on_wednesday: !!watchOnWednesday,
        on_thursday: !!watchOnThursday,
        on_friday: !!watchOnFriday,
        ...(isEdit && reservationSequenceDetail?.id
          ? { reservation_id: reservationSequenceDetail.id }
          : {}),
      };

      getSequenceConflicts(payload)
        .unwrap()
        .then((result) => {
          setTimeConflicts(result ?? null);
          setTimeConflictsChecked(true);
        })
        .catch((e) => {
          console.error('Chyba pri načítaní konfliktov rezervácií', e);
          setTimeConflicts(null);
          setTimeConflictsChecked(true);
        });
    } else {
      setTimeConflicts(null);
      setTimeConflictsChecked(false);
    }
  }, [
    watchRoomId,
    watchDateFrom,
    watchDateTo,
    watchTimeFrom,
    watchTimeTo,
    watchOnMonday,
    watchOnTuesday,
    watchOnWednesday,
    watchOnThursday,
    watchOnFriday,
    watchAllDay,
    isEdit,
    reservationSequenceDetail,
    getSequenceConflicts,
    hasAnyTerms,
  ]);

  // Fetch collided subjects when subject, from or to changes
  const subjectsTimer = useRef();
  useEffect(() => {
    if (!hasAnyTerms) return;
    if (!watchSubjectCode || !watchDateFrom || !watchDateTo) return;

    // Skip when date fields have validation errors
    const hasDateErrors = form
      .getFieldsError(['date_from', 'date_to'])
      .some((field) => Array.isArray(field.errors) && field.errors.length > 0);
    if (hasDateErrors) return;

    // Manual validation mirroring form rules
    const fromDate = dayjs(watchDateFrom);
    const toDate = dayjs(watchDateTo);
    if (!fromDate.isValid() || !toDate.isValid()) return;
    if (watchAllDay) {
      if (toDate.startOf('day').isBefore(fromDate.startOf('day'))) return;
    } else {
      if (!toDate.isAfter(fromDate)) return;
    }

    // All good -> call API
    subjectsTimer.current = setTimeout(() => {
      const subjectsPayload = {
        date_from: fromDate.set('second', 0).format(),
        date_to: toDate.set('second', 0).format(),
        all_day: watchAllDay ? 1 : 0,
        time_from: watchAllDay
          ? '00:00'
          : (dayjs.isDayjs(watchTimeFrom)
              ? watchTimeFrom
              : dayjs(watchTimeFrom)
            ).format('HH:mm'),
        time_to: watchAllDay
          ? '23:59'
          : (dayjs.isDayjs(watchTimeTo)
              ? watchTimeTo
              : dayjs(watchTimeTo)
            ).format('HH:mm'),
        on_monday: !!watchOnMonday,
        on_tuesday: !!watchOnTuesday,
        on_wednesday: !!watchOnWednesday,
        on_thursday: !!watchOnThursday,
        on_friday: !!watchOnFriday,
        code: watchSubjectCode,
      };
      getCollidedSubjects(subjectsPayload);
    }, DEBOUNCE_MS);

    return () => {
      if (subjectsTimer.current) clearTimeout(subjectsTimer.current);
    };
  }, [
    watchSubjectCode,
    watchDateFrom,
    watchDateTo,
    watchTimeFrom,
    watchTimeTo,
    watchOnMonday,
    watchOnTuesday,
    watchOnWednesday,
    watchOnThursday,
    watchOnFriday,
    watchAllDay,
    form,
    getCollidedSubjects,
    hasAnyTerms,
  ]);

  const handleFinish = (values) => {
    const payload = {
      id: isEdit ? id : undefined,
      room_id: values.room_id,
      subject_code: values.subject_code,
      date_from: dayjs(values.date_from).set('second', 0).format(),
      date_to: dayjs(values.date_to).set('second', 0).format(),
      time_from: values.all_day
        ? '00:00'
        : (dayjs.isDayjs(values.time_from)
            ? values.time_from
            : dayjs(values.time_from)
          ).format('HH:mm'),
      time_to: values.all_day
        ? '23:59'
        : (dayjs.isDayjs(values.time_to)
            ? values.time_to
            : dayjs(values.time_to)
          ).format('HH:mm'),
      on_monday: !!values.on_monday,
      on_tuesday: !!values.on_tuesday,
      on_wednesday: !!values.on_wednesday,
      on_thursday: !!values.on_thursday,
      on_friday: !!values.on_friday,
      all_day: values.all_day ? 1 : 0,
      reason: values.reason || null,
      created_for_id: values.created_for_id,
    };
    saveReservationSequence(payload)
      .unwrap()
      .then((resp) => {
        // Some APIs return success=false with 200
        if (resp && resp.success === false) {
          apiErrorMessage(resp);
          return;
        }
        showSaveSuccess();
        navigate(-1);
      })
      .catch((err) => {
        apiErrorMessage(err);
      });
  };

  // Init form with reservation data when editing
  useEffect(() => {
    if (isEdit && reservationSequenceDetail) {
      form.setFieldsValue({
        room_id: reservationSequenceDetail.room_id,
        subject_code: reservationSequenceDetail.subject_code,
        date_from: dayjs(reservationSequenceDetail.date_from),
        date_to: dayjs(reservationSequenceDetail.date_to),
        time_from: dayjs(
          reservationSequenceDetail.date_from,
          DATETIME_FORMAT_TIME,
        ),
        time_to: dayjs(reservationSequenceDetail.date_to, DATETIME_FORMAT_TIME),
        on_monday: reservationSequenceDetail.on_monday === 1,
        on_tuesday: reservationSequenceDetail.on_tuesday === 1,
        on_wednesday: reservationSequenceDetail.on_wednesday === 1,
        on_thursday: reservationSequenceDetail.on_thursday === 1,
        on_friday: reservationSequenceDetail.on_friday === 1,
        all_day: reservationSequenceDetail.all_day === 1,
        created_for_id:
          reservationSequenceDetail.created_for_id !== null &&
          reservationSequenceDetail.created_for_id !== undefined
            ? String(reservationSequenceDetail.created_for_id)
            : currentReservationUserId,
        comment: reservationSequenceDetail.comment || '',
        subject_name: reservationSequenceDetail.subject_name,
        teacher_name: reservationSequenceDetail.teacher_name,
        room_name: reservationSequenceDetail.room_name,
      });
    }
  }, [isEdit, reservationSequenceDetail, form, currentReservationUserId]);

  const isLoading =
    isLoadingReservationSequence ||
    isLoadingTeachers ||
    isLoadingRooms ||
    isLoadingSubjects;

  // Build initial form values: use reservationSequenceDetail if present, otherwise current date/time
  const initialValues = reservationSequenceDetail
    ? {
        ...reservationSequenceDetail,
        date_from: dayjs(reservationSequenceDetail.date_from),
        date_to: dayjs(reservationSequenceDetail.date_to),
        time_from: dayjs(
          reservationSequenceDetail.time_from,
          DATETIME_FORMAT_TIME,
        ),
        time_to: dayjs(reservationSequenceDetail.time_to, DATETIME_FORMAT_TIME),
        auto_accept: false,
      }
    : {
        date_from: dayjs(),
        date_to: dayjs(),
        time_from: dayjs().hour(8).minute(0).second(0).millisecond(0),
        time_to: dayjs().hour(12).minute(0).second(0).millisecond(0),
        created_for_id: currentReservationUserId,
        auto_accept: false,
      };

  if (isLoading) {
    return <Spinner />;
  } else {
    return (
      <>
        <AppBarCMS
          title={pageTitle}
          backUrl={-1}
          backUrlTooltip={t('Rezervácie')}
          permissionsBasic="reservations"
          saveLoading={false}
        />
        <AdminContentBlock title={pageTitle} link={routes.cmsReservations}>
          <ReservationSequenceForm
            form={form}
            mode={mode}
            isEdit={isEdit}
            isCreate={isCreate}
            teachers={teachersForSelect}
            subjects={subjectsForSelect}
            roomsOfDepartment={roomsOfDepartmentForSelect}
            roomsOther={roomsOtherForSelect}
            initialValues={initialValues}
            currentReservationUserId={currentReservationUserId}
            onFinish={handleFinish}
            timeConflicts={timeConflicts}
            timeConflictsChecked={timeConflictsChecked}
            isRoomReservationsAdmin={isRoomReservationsAdmin}
            collidedSubjects={collidedSubjects}
            user={user}
            onTermsAvailabilityChange={setHasAnyTerms}
          />
        </AdminContentBlock>
      </>
    );
  }
};

export default ReservationSequencesEdit;
